import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { LTLAddressBookSnapshot, TempQuoteModel } from '@store/ltl/types';
import { orderStore } from '@store/orders';
import { Address } from '@typeDefinitions/addressTypes';
import {
  LineItemViewModel,
  OrderLineItem,
} from '@typeDefinitions/lineItemTypes';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { mapQuoteBreakdownsToLoadBreakdowns } from '@utils/ltl/loadItemBreakdown';
import _, { flow } from 'lodash';
import { DateTime } from 'luxon';

export const BuildOrderFromQuote = (
  quote: TempQuoteModel,
  address?: Address,
  originSnapshot?: LTLAddressBookSnapshot,
  destinationSnapshot?: LTLAddressBookSnapshot,
) => {
  const load = flow([
    buildOrderPortion,
    buildLoad.bind(this, {
      address: address,
      quote: quote,
      originSnapshot,
      destinationSnapshot,
    }),
  ]);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return load(quote) as IOrderViewModel;
};

const buildOrderPortion = (quote: TempQuoteModel) => {
  return {
    customerId: quote.quoteSpecifics.requestDetails.customerId,
    lineItems: quote.quoteSpecifics.quoteResponse.customerRateQuote
      ? quote.quoteSpecifics.quoteResponse.customerRateQuote.lineItems.map(
          (x: LineItemViewModel) => {
            return {
              operationType: 'Insert',
              rate: x.rate,
              type: x.type,
              description: x.description,
              quantity: x.quantity,
            } as OrderLineItem;
          },
        )
      : quote.quoteSpecifics.quoteResponse.lineItems.map(
          (x: LineItemViewModel) => {
            return {
              operationType: 'Insert',
              rate: x.rate,
              type: x.type,
              description: x.description,
              quantity: x.quantity,
            } as OrderLineItem;
          },
        ),
  } as IOrderViewModel;
};

const buildLoad = (
  param: {
    quote: TempQuoteModel;
    address?: Address;
    originSnapshot?: LTLAddressBookSnapshot;
    destinationSnapshot?: LTLAddressBookSnapshot;
  },
  order: IOrderViewModel,
) => {
  const loadBuildFunc = flow([
    buildLoadPortion,
    buildLoadItems.bind(this, param.quote),
    buildStops.bind(this, param),
    buildQuote.bind(this, param.quote),
    buildLineItems.bind(this, param.quote),
  ]);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const load = loadBuildFunc(param.quote);
  return _.merge({}, { loads: [load] }, order);
};

const buildLoadPortion = (quote: TempQuoteModel): ILoadEditViewModel => {
  return {
    assignedId: 0,
    billingHold: false,
    carrierId: Number(quote.quoteSpecifics.quoteResponse.carrierId),
    canRate: true,
    canVoidLoad: true,
    claim: null,
    id:
      orderStore.order.loads.length > 0
        ? orderStore.order.loads[orderStore.activeTab.index].id
        : 0,
    customerId: quote.quoteSpecifics.requestDetails.customerId ?? 0,
    edi: false,
    mode: 'LTL',
    equipment: 'V, Van',
    enableLoadCarrierRestrictionOverride: false,
    enableTracking: false,
    hasBeenSharedWithCapacity: false,
    hazmatRequired: false,
    hideCustomerName: false,
    loadInformation: undefined,
    ltlQuoteId: quote.ltlQuote.id, //this is dbo.LtlQuote.Id
    newLtlQuoteId: quote.ltlQuoteId, //this is ltl.Quote.Id
    od: false,
    orderId: 0,
    postingId: null,
    status: 'Assigned',
    quoteItems: [],
    quoteStops: [],
    ramps: false,
    sequence: 1,
    signedRateCon: false,
    synched: false,
    tarpRequired: false,
    teamRequired: false,
    trackingEnabled: false,
    weight: _.sum(
      quote.quoteSpecifics.requestDetails.loadItems.flatMap((x) => {
        return x.weight;
      }),
    ),
    quoteId: quote.ltlQuote.quoteId,
    quoteID: quote.ltlQuote.quoteId,
    operationType: 'Insert',
    editable: true,
    isCopied: false,
    carrierActionPlan: false,
  } as unknown as ILoadEditViewModel;
};

const buildLoadItems = (quote: TempQuoteModel, load: ILoadEditViewModel) => {
  const isMixedCommodityOnLoadEnabled = isFeatureFlagEnabled(
    FeatureFlags.LtlSupportMixedCommodityPalletOnLoad,
  )();
  return _.merge(
    {},
    {
      // eslint-disable-next-line complexity
      items: quote.quoteSpecifics.requestDetails.loadItems.map((x) => {
        return {
          // Carry the quote's sub-items onto the load so the stops reflect a requote.
          ...(isMixedCommodityOnLoadEnabled
            ? {
                loadItemBreakdowns: mapQuoteBreakdownsToLoadBreakdowns(
                  x.quoteLoadItemBreakdown,
                ),
              }
            : {}),
          operationType: 'Insert' as OperationTypeV4,
          ltlQuoteLoadItemId: x.ltlQuoteLoadItemId,
          nmfc: x.nmfc,
          class: x.class,
          item: x.type ?? x.item,
          description: x.description,
          quantity: x.quantity,
          charge: x.charge?.toString() ?? '',
          weight: x.weight,
          uom: x.uom,
          type: x.type ?? x.item,
          volume: x.volume ?? 0,
          hazmat: x.hazmat,
          heightInch: x.heightInch ?? 0,
          lengthInch: x.lengthInch ?? 0,
          widthInch: x.widthInch ?? 0,
          pieces: `${x.pieces ?? 0}`,
          displayDimensions: '',
          descriptionWithParanthesis: x.description,
          loadId: 0,
          createdBy: 1,
          loadItemHazmatDetail: {
            hazardClass: x.loadItemHazmatDetail?.hazardClass,
            unNumber: x.loadItemHazmatDetail?.unNumber,
            packingGroup: x.loadItemHazmatDetail?.packingGroup,
            piece: x.loadItemHazmatDetail?.piece,
            contactNumber: x.loadItemHazmatDetail?.contactNumber,
            contactName: x.loadItemHazmatDetail?.contactName,
            loadItemId: 0,
          },
          id: 0,
          orderId: 0,
          pickUpId: -1,
          dropOffId: -2,
        };
      }),
    },
    load,
  );
};

const buildStops = (
  param: {
    quote: TempQuoteModel;
    address?: Address;
    originSnapshot?: LTLAddressBookSnapshot;
    destinationSnapshot?: LTLAddressBookSnapshot;
  },
  load: ILoadEditViewModel,
) => {
  const originAddress = getOriginAddress(param);
  const destinationAddress = getDestinationAddress(param);
  const pickupDate = param.quote.quoteSpecifics.requestDetails.pickupDate;
  const addressZipMatchesOrigin =
    param.address?.zip === param.quote.quoteSpecifics.requestDetails.originZip;
  const originOpenTimeFrom =
    param.originSnapshot?.openTimeFrom ??
    (addressZipMatchesOrigin ? param.address?.openTimeFrom : undefined);
  const originOpenTimeTo =
    param.originSnapshot?.openTimeTo ??
    (addressZipMatchesOrigin ? param.address?.openTimeTo : undefined);

  const estimatedDeliveryDate =
    param.quote.quoteSpecifics.quoteResponse.estimatedDeliveryDate;
  const destDate = estimatedDeliveryDate ?? pickupDate;
  const destOpenTimeFrom = param.destinationSnapshot?.openTimeFrom;
  const destOpenTimeTo = param.destinationSnapshot?.openTimeTo;

  const stops = _.merge(
    {},
    {
      stops: [
        {
          ...originAddress,
          id: -1,
          loadId: 0,
          operationType: 'Insert',
          customerId: param.quote.quoteSpecifics.requestDetails.customerId ?? 0,
          pickUp: true, // the first stop here is the origin
          dropOff: false, // since this is the first stop, its the pickup stop
          needsAppointment: false, // we need to find out if this needs to be true,
          stopDate: pickupDate,
          driverInTime: originOpenTimeFrom
            ? getDriverTimeWithOpenHours(pickupDate, originOpenTimeFrom)
            : getDriverOutInTime('0', pickupDate),
          driverOutTime: originOpenTimeTo
            ? getDriverTimeWithOpenHours(pickupDate, originOpenTimeTo)
            : getDriverOutInTime('0', pickupDate),
          stopDateTime: pickupDate,
          arrived: false,
          timeZoneOffset: 0, // we need to see what the correct value is,
          createdDate: DateTime.now().toUTC().toString(),
          stopOrder: 0,
          shouldSendEdiBooleanVals: {
            shouldSendPickupAppointmentAuto214: false,
            shouldSendDropoffAppointmentAuto214: false,
            shouldSendDropoffDriverInAuto214: false,
            shouldSendPickupDriverInAuto214: false,
            shouldSendPickupDriverOutAuto214: false,
            shouldSendDropoffDriverOutAuto214: false,
          },
        },
        {
          ...destinationAddress,
          id: -2,
          loadId: 0,
          stopOrder: 1,
          operationType: 'Insert',
          driverInTime: destOpenTimeFrom
            ? getDriverTimeWithOpenHours(destDate, destOpenTimeFrom)
            : Boolean(estimatedDeliveryDate)
              ? getDriverOutInTime('0', estimatedDeliveryDate as string)
              : getDriverOutInTime(
                  param.quote.quoteSpecifics.quoteResponse.transitDays,
                  pickupDate,
                ),
          driverOutTime: destOpenTimeTo
            ? getDriverTimeWithOpenHours(destDate, destOpenTimeTo)
            : Boolean(estimatedDeliveryDate)
              ? getDriverOutInTime('0', estimatedDeliveryDate as string)
              : getDriverOutInTime(
                  param.quote.quoteSpecifics.quoteResponse.transitDays,
                  pickupDate,
                ),
          customerId: param.quote.quoteSpecifics.requestDetails.customerId ?? 0,
          needsAppointment: false, // see comment above for the pickup stop
          pickUp: false, // this is the second stop and thus the destination
          dropOff: true, // this is the second stop and thuse the destination and dropoof stop
          stopDate: estimatedDeliveryDate ?? pickupDate,
          stopDateTime: Boolean(estimatedDeliveryDate)
            ? DateTime.fromISO(estimatedDeliveryDate as string).toString()
            : Boolean(param.quote.quoteSpecifics.quoteResponse.transitDays)
              ? DateTime.fromISO(pickupDate, { setZone: false })
                  .plus({
                    days: Number(
                      param.quote.quoteSpecifics.quoteResponse.transitDays,
                    ),
                  })
                  .toISO()
              : DateTime.fromISO(pickupDate).plus({ days: 1 }).toString(),
          arrived: false,
          timeZoneOffset: 0, // we need to see what the correct value is
          createdDate: DateTime.now().toUTC().toString(),
          shouldSendEdiBooleanVals: {
            shouldSendPickupAppointmentAuto214: false,
            shouldSendDropoffAppointmentAuto214: false,
            shouldSendDropoffDriverInAuto214: false,
            shouldSendPickupDriverInAuto214: false,
            shouldSendPickupDriverOutAuto214: false,
            shouldSendDropoffDriverOutAuto214: false,
          },
        },
      ] as LastUsedTopStopModel[],
    },
    load,
  );
  return stops;
};

const getDriverOutInTime = (
  expectedTransitDays: string,
  date?: string | null,
) => {
  const days = Number(expectedTransitDays);
  if (date === null || date === undefined) return '';

  const target = DateTime.fromISO(date, { setZone: false }).plus({
    days: days,
  });
  // Return a naive local datetime (no UTC offset), matching
  // getDriverTimeWithOpenHours/stopWindowTimeFromStore, otherwise the
  // embedded offset gets normalized to UTC server-side and midnight comes
  // back displayed as a shifted time (e.g. 4am during EDT).
  return `${target.toISODate()}T00:00:00`;
};

const getDriverTimeWithOpenHours = (date: string, openTime: string): string => {
  if (!openTime || !date) return getDriverOutInTime('0', date);
  const baseDate = DateTime.fromISO(date, { setZone: false });
  // openTime may be a full ISO string or just a time string like "08:00" / "08:00:00"
  const isoTime = openTime.includes('T')
    ? openTime
    : `${baseDate.toISODate()}T${openTime}`;
  const timePart = DateTime.fromISO(isoTime);
  if (!timePart.isValid) return getDriverOutInTime('0', date);
  return `${baseDate.toISODate()}T${timePart.toFormat('HH:mm:ss')}`;
};

const getOriginAddress = (param: {
  quote: TempQuoteModel;
  address?: Address;
  originSnapshot?: LTLAddressBookSnapshot;
}) => {
  if (param.originSnapshot) {
    return {
      locationName: param.originSnapshot.locationName,
      zip:
        param.originSnapshot.zip ??
        param.quote.quoteSpecifics.requestDetails.originZip,
      city:
        param.originSnapshot.city ??
        param.quote.quoteSpecifics.requestDetails.originCity,
      state:
        param.originSnapshot.state ??
        param.quote.quoteSpecifics.requestDetails.originState,
      countryCode:
        param.originSnapshot.countryCode ??
        param.quote.quoteSpecifics.requestDetails.originCountry,
      contact: param.originSnapshot.contactName,
      phone: param.originSnapshot.contactPhone,
      email: param.originSnapshot.contactEmail,
      address1: param.originSnapshot.addressLine1,
      address2: param.originSnapshot.addressLine2,
      instructions: param.originSnapshot.specialInstructions,
      description: param.originSnapshot.internalNote,
      directions: param.originSnapshot.drivingDirections,
    };
  }
  if (
    param.address &&
    param.address.zip === param.quote.quoteSpecifics.requestDetails.originZip
  ) {
    return {
      locationName: param.address.name ?? '',
      zip: param.address.zip,
      city: param.address.city,
      state: param.address.state,
      countryCode: param.address.countryCode,
      contact: param.address.contactName,
      phone: param.address.contactPhone,
      address1: param.address.addressLine1,
      instructions: param.address.specialInstructions,
    };
  }
  return {
    locationName: '',
    zip: param.quote.quoteSpecifics.requestDetails.originZip,
    state: param.quote.quoteSpecifics.requestDetails.originState,
    city: param.quote.quoteSpecifics.requestDetails.originCity,
    countryCode: param.quote.quoteSpecifics.requestDetails.originCountry,
    address1: '',
    address2: '',
    contact: '',
    phone: '',
    email: '',
    instructions: '',
    description: '',
    directions: '',
  };
};

const getDestinationAddress = (param: {
  quote: TempQuoteModel;
  destinationSnapshot?: LTLAddressBookSnapshot;
}) => {
  if (param.destinationSnapshot) {
    return {
      locationName: param.destinationSnapshot.locationName,
      zip:
        param.destinationSnapshot.zip ??
        param.quote.quoteSpecifics.requestDetails.destinationZip,
      city:
        param.destinationSnapshot.city ??
        param.quote.quoteSpecifics.requestDetails.destinationCity,
      state:
        param.destinationSnapshot.state ??
        param.quote.quoteSpecifics.requestDetails.destinationState,
      countryCode:
        param.destinationSnapshot.countryCode ??
        param.quote.quoteSpecifics.requestDetails.destinationCountry,
      contact: param.destinationSnapshot.contactName,
      phone: param.destinationSnapshot.contactPhone,
      email: param.destinationSnapshot.contactEmail,
      address1: param.destinationSnapshot.addressLine1,
      address2: param.destinationSnapshot.addressLine2,
      instructions: param.destinationSnapshot.specialInstructions,
      description: param.destinationSnapshot.internalNote,
      directions: param.destinationSnapshot.drivingDirections,
    };
  }
  return {
    locationName: '',
    zip: param.quote.quoteSpecifics.requestDetails.destinationZip,
    state: param.quote.quoteSpecifics.requestDetails.destinationState,
    city: param.quote.quoteSpecifics.requestDetails.destinationCity,
    countryCode: param.quote.quoteSpecifics.requestDetails.destinationCountry,
    address1: '',
    address2: '',
    contact: '',
    phone: '',
    email: '',
    instructions: '',
    description: '',
    directions: '',
  };
};

const buildQuote = (quote: TempQuoteModel, load: ILoadEditViewModel) => {
  return _.merge(
    {},
    {
      ltlQuote: {
        ...quote.ltlQuote,
        ltlQuoteLoadItems: quote.ltlQuote.ltlQuoteLoadItems,
      },
    },
    load,
  );
};

const buildLineItems = (quote: TempQuoteModel, load: ILoadEditViewModel) => {
  return _.merge(
    {},
    {
      lineItems: quote.quoteSpecifics.quoteResponse.lineItems.map(
        (x: LineItemViewModel) => {
          return {
            id: x.id,
            quantity: x.quantity,
            rate: x.rate,
            readOnly: false,
            synched: false,
            type: x.type,
            description: x.description,
            operationType: 'Insert',
          };
        },
      ) as LineItemViewModel[],
    },
    load,
  );
};
